// ============================================
// FAQ 员工提问弹窗
// ============================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, HelpCircle, Loader2, LogIn, Save, Send, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import { useFaqQuestionStore } from './faq-question-store';
import FaqAuthModal from './FaqAuthModal';
import FaqRichEditor from './FaqRichEditor';
import { uploadFaqImage } from './faq-service';
import { EMPTY_TIPTAP_DOC, ensureTiptapContent, richTextHasContent, tiptapToPlainText } from './faq-rich-content';
import { useFaqSpace } from './FaqSpaceContext';
import type { FaqItem, FaqQuestion } from './types';

interface FaqQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  relatedFaq?: FaqItem | null;
  editingQuestion?: FaqQuestion | null;
  onSaved?: (question: FaqQuestion) => void;
}

type RichEditorHandle = {
  getJSON: () => unknown;
  isDestroyed?: boolean;
};

export default function FaqQuestionModal({
  isOpen,
  onClose,
  relatedFaq,
  editingQuestion,
  onSaved,
}: FaqQuestionModalProps) {
  const navigate = useNavigate();
  const { space, path } = useFaqSpace();
  const { user } = useAuthStore();
  const { createQuestion, updateQuestion, saving } = useFaqQuestionStore();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState(EMPTY_TIPTAP_DOC);
  const [contentEditorKey, setContentEditorKey] = useState(0);
  const [error, setError] = useState('');
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const contentEditorRef = useRef<RichEditorHandle | null>(null);
  const pendingUploadTasksRef = useRef<Set<Promise<void>>>(new Set());
  const isEditing = Boolean(editingQuestion);
  const uploadImage = (file: File) => uploadFaqImage(file, space.slug);

  const registerUploadTask = useCallback((task: Promise<void>) => {
    pendingUploadTasksRef.current.add(task);
    setPendingUploadCount(pendingUploadTasksRef.current.size);

    const cleanup = () => {
      pendingUploadTasksRef.current.delete(task);
      setPendingUploadCount(pendingUploadTasksRef.current.size);
    };
    task.then(cleanup, cleanup);
  }, []);

  const waitForPendingUploads = useCallback(async () => {
    while (pendingUploadTasksRef.current.size > 0) {
      await Promise.allSettled(Array.from(pendingUploadTasksRef.current));
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(editingQuestion?.title || (relatedFaq ? `关于「${relatedFaq.question}」的问题` : ''));
    const initialContent = editingQuestion
      ? ensureTiptapContent(editingQuestion.content, editingQuestion.content_format)
      : EMPTY_TIPTAP_DOC;
    setContent(initialContent);
    setContentEditorKey((key) => key + 1);
    contentEditorRef.current = null;
    pendingUploadTasksRef.current.clear();
    setPendingUploadCount(0);
    setError('');
  }, [isOpen, relatedFaq, editingQuestion]);

  if (!isOpen) return null;

  async function handleSubmit() {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!title.trim()) {
      setError('请填写问题标题');
      return;
    }
    let currentContent = content;
    if (pendingUploadTasksRef.current.size > 0) {
      setError('图片上传中，上传完成后会继续提交...');
      await waitForPendingUploads();
    }

    const editor = contentEditorRef.current;
    if (editor && !editor.isDestroyed) {
      currentContent = JSON.stringify(editor.getJSON());
      setContent(currentContent);
    }

    if (currentContent.includes('blob:')) {
      setError('图片尚未完成上传，请等待图片上传完成后再提交');
      return;
    }

    if (!richTextHasContent(currentContent)) {
      setError('请补充问题描述，方便管理员判断');
      return;
    }

    setError('');
    try {
      const question = isEditing && editingQuestion
        ? await updateQuestion(editingQuestion.id, {
            title,
            content: currentContent,
            contentFormat: 'tiptap',
          })
        : await createQuestion({
            title,
            content: currentContent,
            contentFormat: 'tiptap',
            relatedFaqId: relatedFaq?.id,
          });
      onSaved?.(question);
      onClose();
      if (!isEditing) {
        navigate(path(`questions/${question.id}`));
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : '';
      setError(message || (isEditing ? '保存失败，请稍后重试' : '提交失败，请稍后重试'));
    }
  }

  const dialogClassName = isEditing
    ? 'relative flex h-[calc(100dvh-1.5rem)] w-full max-w-none flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'
    : 'relative mx-4 flex max-h-[calc(100dvh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl';
  const headerClassName = isEditing
    ? 'flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-7'
    : 'flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-7';
  const bodyClassName = isEditing
    ? 'flex-1 space-y-4 overflow-y-auto px-5 py-5 sm:px-7'
    : 'flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-7';
  const footerClassName = isEditing
    ? 'flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:px-7'
    : 'flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 bg-slate-50 px-5 py-4 sm:px-7';
  const editorMinHeight = isEditing ? 'calc(100dvh - 360px)' : '320px';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-5">
        <div className="absolute inset-0 bg-slate-900/35" onClick={onClose} />
        <div className={dialogClassName}>
          <div className={headerClassName}>
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-sky-50 text-sky-600">
                <HelpCircle size={18} />
              </span>
              <div>
                <h3 className="text-base font-bold text-slate-800">{isEditing ? '编辑留言' : '在线提问'}</h3>
                <p className="text-xs text-slate-400">
                  {isEditing ? '更新你的留言内容，管理员可继续查看上下文' : '留言会同步给 FAQ 管理员处理'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              aria-label="关闭"
            >
              <X size={18} />
            </button>
          </div>

          <div className={bodyClassName}>
            {!user && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">请先登录后再提问</p>
                    <p className="mt-0.5 text-xs text-amber-600">登录后可以查看留言回复和邮件通知状态。</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAuthModal(true)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-600"
                >
                  <LogIn size={13} />
                  登录 / 注册
                </button>
              </div>
            )}

            {relatedFaq && (
              <div className="rounded-xl border border-sky-100 bg-sky-50/60 px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-500">关联 FAQ</p>
                <p className="mt-1 line-clamp-2 text-sm font-medium text-slate-700">{relatedFaq.question}</p>
              </div>
            )}

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">问题标题</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="请用一句话描述你的问题"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                maxLength={160}
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-slate-500">问题描述</label>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 transition focus-within:border-sky-300 focus-within:ring-2 focus-within:ring-sky-100">
                <FaqRichEditor
                  key={`${editingQuestion?.id ?? relatedFaq?.id ?? 'new'}-${contentEditorKey}`}
                  content={content}
                  onChange={setContent}
                  placeholder="补充具体场景、系统页面、报错信息，可直接粘贴或上传截图"
                  uploadImage={uploadImage}
                  minHeight={editorMinHeight}
                  onEditorReady={(editor) => {
                    contentEditorRef.current = editor as RichEditorHandle;
                  }}
                  onUploadTask={registerUploadTask}
                />
              </div>
              <p className="mt-1 text-right text-[11px] text-slate-400">
                {pendingUploadCount > 0 ? `图片上传中（${pendingUploadCount}）` : `${tiptapToPlainText(content).length}/20000`}
              </p>
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
                {error}
              </div>
            )}
          </div>

          <div className={footerClassName}>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl px-4 py-2 text-sm font-medium text-slate-500 transition hover:bg-slate-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || pendingUploadCount > 0}
              className="inline-flex items-center gap-1.5 rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600 disabled:opacity-60"
            >
              {saving || pendingUploadCount > 0 ? <Loader2 size={15} className="animate-spin" /> : isEditing ? <Save size={15} /> : <Send size={15} />}
              {pendingUploadCount > 0 ? '图片上传中...' : isEditing ? '保存修改' : '提交留言'}
            </button>
          </div>
        </div>
      </div>

      <FaqAuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}
